import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";

const pageElementsVariants = cva("", {
    variants: {
        variant: {
            container: "container mx-auto px-4 sm:px-6 lg:px-8",
            section: "py-8 sm:py-12 lg:py-16",
            card: "rounded-lg border bg-card text-card-foreground shadow-sm",
            header: "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
            footer: "border-t bg-muted/50",
        },
    },
    defaultVariants: {
        variant: "container",
    },
});

export interface PageElementProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pageElementsVariants> {
    as?: React.ElementType;
}

export const PageElement = React.forwardRef<HTMLDivElement, PageElementProps>(
    ({ className, variant, as: Component = "div", ...props }, ref) => {
        return (
            <Component
                ref={ref}
                className={cn(pageElementsVariants({ variant }), className)}
                {...props}
            />
        );
    }
);

PageElement.displayName = "PageElement";

// Specific page elements for common use cases
export const Container = React.forwardRef<HTMLDivElement, Omit<PageElementProps, 'variant'>>(
    (props, ref) => <PageElement ref={ref} variant="container" {...props} />
);

export const Section = React.forwardRef<HTMLDivElement, Omit<PageElementProps, 'variant'>>(
    (props, ref) => <PageElement ref={ref} variant="section" {...props} />
);

export const PageCard = React.forwardRef<HTMLDivElement, Omit<PageElementProps, 'variant'>>(
    (props, ref) => <PageElement ref={ref} variant="card" {...props} />
);

export const Header = React.forwardRef<HTMLDivElement, Omit<PageElementProps, 'variant'>>(
    (props, ref) => <PageElement ref={ref} variant="header" as="header" {...props} />
);

export const Footer = React.forwardRef<HTMLDivElement, Omit<PageElementProps, 'variant'>>(
    (props, ref) => <PageElement ref={ref} variant="footer" as="footer" {...props} />
);

// Legacy components for backward compatibility
export interface PageTitleProps extends React.HTMLAttributes<HTMLDivElement> {
    title?: string;
    /** Reports menubar rendered right after the title (in-game report header). */
    menubar?: React.ReactNode;
    /** Report pages: small text left of the bar (a report's date), on paper too. */
    leading?: React.ReactNode;
    children?: React.ReactNode;
}

/**
 * A REPORT page's header: the reports menubar names the report on screen, so
 * the <h1> is for paper only (the bar is `print-hide` — without it a printed
 * report is an anonymous table). Every report renders this one, so switching
 * between them nothing moves.
 */
export const ReportTitle = React.forwardRef<HTMLDivElement, PageTitleProps>(
    ({ title, menubar, leading, children, className, ...props }, ref) => (
        <div ref={ref} className={cn("mb-4", className)} {...props}>
            {title ? (
                <h1 className="print-only text-left text-2xl font-semibold">{title}</h1>
            ) : null}
            {/* The bar sits in the same place on every report, whatever its
                buttons: centred with them to its right where they fit (xl),
                else first in the row with them at the far right — and below
                1000px (every iPad held upright: 744–834), on a row of their
                own on every report alike, whatever their width: at 820 the
                Production Summary's no longer fit beside the bar and the
                Unit Report's did, so switching between them jumped. They
                wrap rather than cover the bar (the centred
                row let them spill over it on an iPad), and the row is
                top-aligned so a wrapped one never moves the bar down. Leading
                text and buttons are the bar's height (38px), so on one row
                everything is centred on it. */}
            <div className="flex w-full flex-wrap items-start gap-x-4 gap-y-2 xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <div className="min-w-0 max-w-full xl:col-start-2 xl:row-start-1 xl:justify-self-center [&>nav]:max-w-full">
                    {menubar}
                </div>
                {/* Only when there is some: an empty one would still take a
                    gap, and push the buttons to a second row sooner. */}
                {leading ? (
                    <div className="flex min-h-[38px] min-w-0 flex-1 items-center xl:col-start-1 xl:row-start-1">
                        {leading}
                    </div>
                ) : null}
                {/* print-hide: action buttons are chrome, never paper. */}
                <div className="print-hide ml-auto flex min-h-[38px] min-w-0 flex-wrap items-center justify-end gap-2 max-[1000px]:basis-full xl:col-start-3 xl:row-start-1">
                    {children}
                </div>
            </div>
        </div>
    )
);

export const PageTitle = React.forwardRef<HTMLDivElement, PageTitleProps>(
    ({ title, menubar, leading, children, className, ...props }, ref) => {
        // A page with a reports menubar is a report (above). Pages without
        // one (Practice Scripts, Game Library) keep their visible title.
        if (menubar) {
            return (
                <ReportTitle ref={ref} title={title} menubar={menubar} leading={leading} className={className} {...props}>
                    {children}
                </ReportTitle>
            );
        }
        return (
            <div ref={ref} className={cn("mb-4", className)} {...props}>
                <div
                    className={cn(
                        "flex w-full items-center",
                        title ? "justify-between" : "justify-end"
                    )}
                >
                    {title ? (
                        <h1 className="w-full py-4 text-left text-2xl font-semibold">{title}</h1>
                    ) : null}
                    {/* print-hide: action buttons are chrome, never paper —
                        same as the QB copy of this component. */}
                    <div className="print-hide flex items-center gap-x-2">{children}</div>
                </div>
            </div>
        );
    }
);

export interface PageWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
    innerContainerClassName?: string;
}

export const PageWrapper = React.forwardRef<HTMLDivElement, PageWrapperProps>(
    ({ children, className, innerContainerClassName, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn("flex min-h-full grow flex-col", className)}
                {...props}
            >
                <div
                    className={cn(
                        "rounded-sm bg-white p-2",
                        innerContainerClassName
                    )}
                >
                    {children}
                </div>
            </div>
        );
    }
);

Container.displayName = "Container";
Section.displayName = "Section";
PageCard.displayName = "PageCard";
Header.displayName = "Header";
Footer.displayName = "Footer";
PageTitle.displayName = "PageTitle";
ReportTitle.displayName = "ReportTitle";
PageWrapper.displayName = "PageWrapper";